import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, CircularProgress } from '@mui/material'
import { useInbox, documentUrl } from '@/hooks/useInbox'
import {
  INBOX_QUEUE_STATES,
  INBOX_STATE_LABELS,
  fileMeta,
  kindMeta,
  ratingDot,
  scoreMeta,
  type InboxItem,
  type InboxItemKind,
  type InboxItemState,
} from '@/types/inbox'
import {
  Button,
  Dot,
  EmptyState,
  FileTag,
  FilterBar,
  Icon,
  IconButton,
  Notice,
  Page,
  PageHeader,
  Pill,
  SearchField,
  Select,
  Tabs,
} from '@/components/ui'
import { InboxDetailDrawer } from '@/components/Inbox/InboxDetailDrawer'
import { formatAmount, formatDate, formatTime } from '@/utils/inboxFormat'
import { useT } from '@/i18n/useT'

type KindFilter = InboxItemKind | 'all'
type SortKey = 'received' | 'amount' | 'risk'

/** Two-row card: dark tile with the headline, paper row with the details. */
function InboxRow({
  item,
  onOpen,
  onPreview,
  onDelete,
}: {
  item: InboxItem
  onOpen: () => void
  onPreview: () => void
  onDelete: () => void
}) {
  const { t } = useT()
  const file = fileMeta(item)
  const kind = kindMeta(item.kind)
  const score = scoreMeta(item)
  const warnings = item.warnings ?? []

  const invoiced = item.flixpectorTotalAmount ?? item.totalGross
  const agreed = item.flixpectorFinalAmount
  const hasDiscount = agreed != null && invoiced != null && agreed < invoiced

  return (
    <div
      onClick={onOpen}
      className="fx-card-hoverable"
      style={{
        border: '1px solid var(--line)',
        borderRadius: 12,
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          padding: '11px 16px',
          background: 'var(--tile)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Dot color={ratingDot(item)} />
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: '#fff',
            flex: 1,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.supplierName || item.subject || t('(no subject)')}
        </div>
        {warnings.length > 0 && (
          <div
            title={warnings.join(' · ')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
              color: '#ffc068',
              flexShrink: 0,
            }}
          >
            <Icon name="warning" size={15} strokeWidth={2.2} />
            {warnings.length}
          </div>
        )}
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0,
            minWidth: 86,
            textAlign: 'right',
          }}
        >
          {formatDate(item.invoiceDate || item.receivedAt || item.createdAt)}
        </div>
      </div>

      <div
        style={{
          padding: '11px 16px',
          background: 'var(--paper)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <FileTag label={file.tag} fg={file.fg} bg={file.bg} />
        <div
          style={{
            fontSize: 12,
            color: 'var(--muted)',
            flex: 1,
            minWidth: 120,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {(item.filename || t('No attachment')) +
            ' · ' +
            (item.originalSender || item.fromAddress || '')}
        </div>
        <Pill fg={kind.fg} bg={kind.bg} border={kind.border}>
          {t(kind.label)}
        </Pill>
        <Pill>{t(INBOX_STATE_LABELS[item.state])}</Pill>
        {item.assigneeInitials && (
          <Pill
            fg="var(--kSrvFg)"
            bg="var(--kSrvBg)"
            border="var(--kSrvBd)"
            style={{ padding: '4px 8px' }}
          >
            <span title={`${t('Handler')}: ${item.assigneeName ?? item.assigneeInitials}`}>
              {item.assigneeInitials}
            </span>
          </Pill>
        )}
        {/* Fixed slot so the columns stay aligned, but only filled once
            Flixpector has actually scored the invoice. */}
        <div
          title={
            score.score != null
              ? t(score.title).replace('{score}', score.score.toFixed(1))
              : t(score.title)
          }
          style={{
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
            minWidth: 84,
            textAlign: 'right',
            color: score.fg,
          }}
        >
          {item.flixpectorScore != null ? `${t('Risk')} ${score.text}` : ''}
        </div>
        <div style={{ flexShrink: 0, minWidth: 104, textAlign: 'right' }}>
          {hasDiscount && (
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                textDecoration: 'line-through',
                color: 'var(--strike)',
                lineHeight: 1.25,
              }}
            >
              {formatAmount(invoiced, item.currency)}
            </div>
          )}
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.3,
              color: hasDiscount ? 'var(--ok)' : 'var(--text)',
            }}
          >
            {formatAmount(hasDiscount ? agreed : item.totalGross, item.currency)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          <IconButton title={t('Show the original document')} onClick={onPreview}>
            <Icon name="eye" size={16} />
          </IconButton>
          <IconButton title={t('Delete')} variant="danger" onClick={onDelete}>
            <Icon name="trash" size={16} />
          </IconButton>
        </div>
      </div>
    </div>
  )
}

export default function InboxPage() {
  const {
    items,
    counts,
    mailbox,
    lastSyncedAt,
    stateFilter,
    setStateFilter,
    loading,
    error,
    refresh,
    accept,
    sendToFlixpector,
    remove,
    dismiss,
    changeKind,
    syncFlixpector,
    loadPreview,
  } = useInbox('new')

  const { t } = useT()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [kindFilter, setKindFilter] = useState<KindFilter>('all')
  const [sort, setSort] = useState<SortKey>('received')

  const selected = useMemo(
    () => items.find((i) => i.id === selectedId) ?? null,
    [items, selectedId],
  )

  // An action may have removed the open item; close rather than show a stale one
  useEffect(() => {
    if (selectedId && !items.some((i) => i.id === selectedId)) setSelectedId(null)
  }, [items, selectedId])

  const run = useCallback(
    async (label: string, fn: () => Promise<void>, closeAfter = true) => {
      setBusy(true)
      setActionError(null)
      try {
        await fn()
        setNotice(label)
        if (closeAfter) setSelectedId(null)
      } catch (e) {
        setActionError(e instanceof Error ? e.message : t('Action failed'))
      } finally {
        setBusy(false)
      }
    },
    [t],
  )

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    let list = items
    if (kindFilter !== 'all') list = list.filter((i) => i.kind === kindFilter)
    if (q) {
      list = list.filter((i) =>
        [i.supplierName, i.subject, i.invoiceNumber, i.filename, i.originalSender]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
    }

    const sorted = [...list]
    if (sort === 'amount') {
      sorted.sort((a, b) => (b.totalGross ?? 0) - (a.totalGross ?? 0))
    } else if (sort === 'risk') {
      // Most warnings first, then the higher Flixpector risk score
      sorted.sort((a, b) => {
        const w = (b.warnings?.length ?? 0) - (a.warnings?.length ?? 0)
        if (w !== 0) return w
        return (b.flixpectorScore ?? -1) - (a.flixpectorScore ?? -1)
      })
    } else {
      sorted.sort(
        (a, b) =>
          new Date(b.receivedAt ?? b.createdAt).getTime() -
          new Date(a.receivedAt ?? a.createdAt).getTime(),
      )
    }
    return sorted
  }, [items, kindFilter, query, sort])

  // The API cannot tell us when the mailbox itself was last polled, only when
  // this page last read it — so the line says exactly that, nothing more.
  const syncLine = [
    mailbox ?? t('Mail ingestion not configured'),
    lastSyncedAt ? `${t('last loaded')} ${formatTime(lastSyncedAt)}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Page>
      <PageHeader
        title={t('Inbox')}
        actions={
          <>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{syncLine}</div>
            <Button variant="outline" onClick={() => void refresh()} disabled={loading}>
              <Icon name="refresh" size={15} strokeWidth={2.2} />
              {t('Sync mailbox')}
            </Button>
          </>
        }
      />

      <Tabs<InboxItemState>
        value={stateFilter}
        onChange={(v) => {
          setStateFilter(v)
          setSelectedId(null)
        }}
        tabs={INBOX_QUEUE_STATES.map((state) => ({
          value: state,
          label: t(INBOX_STATE_LABELS[state]),
          count: counts[state],
        }))}
      />

      <FilterBar>
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder={t('Supplier, subject, invoice number, file …')}
        />
        <Select<KindFilter>
          value={kindFilter}
          onChange={setKindFilter}
          ariaLabel={t('Document kind')}
          minWidth={190}
          options={[
            { value: 'all', label: t('All document kinds') },
            { value: 'invoice', label: t('Invoice') },
            { value: 'service_description', label: t('Service record') },
            { value: 'other', label: t('Mail without a document') },
          ]}
        />
        <Select<SortKey>
          value={sort}
          onChange={setSort}
          ariaLabel={t('Sorting')}
          minWidth={180}
          options={[
            { value: 'received', label: t('Newest first') },
            { value: 'amount', label: t('Highest amount') },
            { value: 'risk', label: t('Flagged first') },
          ]}
        />
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          {t('{n} of {total} entries')
            .replace('{n}', String(visible.length))
            .replace('{total}', String(items.length))}
        </div>
      </FilterBar>

      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Notice onClose={() => setNotice(null)}>{notice}</Notice>}

      {loading && items.length === 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <CircularProgress size={28} />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visible.map((item) => (
          <InboxRow
            key={item.id}
            item={item}
            onOpen={() => {
              setActionError(null)
              setSelectedId(item.id)
            }}
            onPreview={() => {
              if (!item.documentId) {
                setNotice(t('This mail has no attachment — see the content in the detail view.'))
                setSelectedId(item.id)
                return
              }
              window.open(documentUrl(item.documentId, true), '_blank', 'noopener')
            }}
            onDelete={() => void run(t('Entry deleted.'), () => remove(item.id))}
          />
        ))}
      </div>

      {!loading && visible.length === 0 && (
        <EmptyState
          title={t('No entries')}
          hint={t('Nothing in the inbox matches this filter right now.')}
        />
      )}

      <InboxDetailDrawer
        item={selected}
        busy={busy}
        error={actionError}
        onClose={() => setSelectedId(null)}
        loadPreview={loadPreview}
        onChangeKind={(kind) =>
          selected &&
          void run(t('Document kind changed.'), () => changeKind(selected.id, kind), false)
        }
        onDelete={() => selected && void run(t('Entry deleted.'), () => remove(selected.id))}
        onDismiss={() => selected && void run(t('Marked as done.'), () => dismiss(selected.id))}
        onSend={(assigneeUserId) =>
          selected &&
          void run(t('Handed over to Flixpector for review.'), () =>
            sendToFlixpector(selected.id, assigneeUserId),
          )
        }
        onAccept={() =>
          selected && void run(t('Added to the database.'), () => accept(selected.id))
        }
        onSync={() =>
          selected &&
          void run(t('Result fetched from Flixpector.'), () => syncFlixpector(selected.id), false)
        }
        onSyncBack={() =>
          selected &&
          void run(t('Result fetched — the entry is open in the inbox again.'), () =>
            syncFlixpector(selected.id, true),
          )
        }
      />
    </Page>
  )
}
